// Model step, "Wannier90" tab: a tight-binding model from Wannier90 output - _hr.dat (one per spin for a collinear
// spin-polarised run), the .wout (or .win + _centres.xyz), optional _wsvec.dat - with the spin variant, the DFT
// Fermi energy and hopping cutoffs. The server reads the files into a model file (negf.model, prayog_negf.wannier).
import { Alert, Button, FileButton, Group, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { notifyError } from "../../components/Errors";
import { post, uploadFile, type Job, type ProjectDetail } from "../../lib/api";
import { defaultWannier, guessWannierFiles, WANNIER_SPINS, wannierParams, wannierProblems, wannierRoles, type NegfMemory,
  type WannierForm, type WannierRole, type WannierSpin } from "../../lib/negf";

export function WannierPanel({ project, mem, remember, update, started, product = "negf" }: {
  project: ProjectDetail; mem: NegfMemory; remember: (m: Partial<NegfMemory>) => void; update?: (m: Partial<NegfMemory>) => void;
  started: (j: Job, source: NegfMemory["source"]) => void; product?: "negf" | "properties";
}) {
  const qc = useQueryClient();
  const w = mem.wannier ?? defaultWannier();
  const set = (next: Partial<WannierForm>) => (update ?? remember)({ wannier: { ...w, ...next } });
  const uploads = useMemo(() => (project.files ?? []).filter((f) => f.kind === "input" && f.status === "ready"), [project.files]);
  const options = uploads.map((f) => ({ value: f.id, label: f.name }));

  // pick the uploads by name (newest first) whenever the spin variant or the uploads change
  useEffect(() => {
    const g = guessWannierFiles(w, uploads);
    if (JSON.stringify(g) !== JSON.stringify(w.files)) set({ files: g });
  }, [w.spin, w.geometry, uploads]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = useMutation({
    mutationFn: async ({ role, file }: { role: WannierRole; file: File }) => ({ role, stored: await uploadFile(project.id, file) }),
    onSuccess: ({ role, stored }) => {
      set({ files: { ...w.files, [role]: stored.id }, seed: w.seed || seedOf(stored.name) });
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
    onError: (e) => notifyError(e),
  });
  const build = useMutation({
    mutationFn: () => post<Job>("/jobs", { kind: `${product}.model`, project_id: project.id, params: { wannier: wannierParams(w) } }),
    onSuccess: (j) => { remember({ wannier: w }); started(j, { kind: "wannier", name: w.seed.trim() || "wannier", spin: w.spin }); },
    onError: (e) => notifyError(e),
  });
  const problems = wannierProblems(w);

  return (
    <Stack gap="sm" data-testid="wannier-panel">
      <Text size="sm" c="dimmed">A tight-binding model from Wannier90: each Wannier function joins its nearest atom, and the atoms that
        carry them become the device's sites. Energies are shifted so that E_F = 0.</Text>
      <div>
        <Text size="sm" fw={500} mb={4}>Spin</Text>
        <SegmentedControl fullWidth value={w.spin} onChange={(v) => set({ spin: v as WannierSpin })} data={WANNIER_SPINS} data-testid="wannier-spin" />
        <Text size="xs" c="dimmed" mt={4}>
          {w.spin === "none" && "Spin-degenerate Wannier functions from one run (one _hr.dat)."}
          {w.spin === "collinear" && "A spin-polarised run wannierised per spin (spin = up / down): two _hr.dat files with the same projections. Gives spin-resolved transmission."}
          {w.spin === "spinor" && "Spinor Wannier functions (spinors = .true., spin-orbit coupling): one _hr.dat holding both spins."}
        </Text>
      </div>
      <SegmentedControl size="xs" value={w.geometry} onChange={(v) => set({ geometry: v as WannierForm["geometry"] })}
        data={[{ value: "wout", label: "Geometry from .wout" }, { value: "win", label: ".win + _centres.xyz" }]} />
      {wannierRoles(w).map((r) => (
        <Group key={r.role} gap="xs" align="flex-end" wrap="nowrap">
          <Select style={{ flex: 1 }} size="xs" label={r.label} placeholder={r.required ? "choose an upload" : "none"} data={options}
            value={w.files[r.role] ?? null} clearable={!r.required} searchable
            onChange={(v) => set({ files: { ...w.files, [r.role]: v ?? undefined } })} />
          <FileButton onChange={(f) => f && upload.mutate({ role: r.role, file: f })}>
            {(props) => <Button size="xs" variant="default" loading={upload.isPending && upload.variables?.role === r.role} {...props}>Upload</Button>}
          </FileButton>
        </Group>
      ))}
      {w.spin === "spinor" && (
        <Select size="xs" label="Order of the spin partners" allowDeselect={false} value={w.spinor_order}
          onChange={(v) => set({ spinor_order: (v ?? "auto") as WannierForm["spinor_order"] })}
          data={[{ value: "auto", label: "Automatic (smallest spin-flip couplings)" }, { value: "interleaved", label: "Interleaved: 1↑ 1↓ 2↑ 2↓ …" },
            { value: "blocks", label: "Blocks: all ↑, then all ↓" }]} />
      )}
      <SimpleGrid cols={3} spacing={6}>
        <NumberInput size="xs" label="Fermi energy" description="of the DFT run" rightSection={<Text size="xs" c="dimmed">eV</Text>}
          value={w.efermi ?? ""} decimalScale={6} onChange={(v) => set({ efermi: v === "" ? null : Number(v) })} />
        <NumberInput size="xs" label="Drop hoppings below" description="|H| smaller than" rightSection={<Text size="xs" c="dimmed">eV</Text>}
          value={w.hop_cutoff} min={0} step={0.001} decimalScale={6} onChange={(v) => set({ hop_cutoff: Number(v) || 0 })} />
        <NumberInput size="xs" label="Longest hopping" description="0 = keep all" rightSection={<Text size="xs" c="dimmed">Å</Text>}
          value={w.max_range} min={0} step={1} onChange={(v) => set({ max_range: Number(v) || 0 })} />
      </SimpleGrid>
      <TextInput size="xs" label="Model name" placeholder="seedname" value={w.seed} onChange={(e) => set({ seed: e.currentTarget.value })} />
      <Text size="xs" c="dimmed">Long hoppings make the leads longer (the lead unit must cover the longest hopping along the transport
        direction); a range of 10–15 Å or a cutoff of a few meV keeps devices small.</Text>
      {problems.length > 0 && <Alert color="gray" p="xs"><Text size="xs">{problems.join(" ")}</Text></Alert>}
      <Group><Button onClick={() => build.mutate()} loading={build.isPending} disabled={problems.length > 0}>Build the model</Button></Group>
    </Stack>
  );
}

/** "graphene" from "graphene_hr.dat", "fe_up" -> "fe" etc. */
export function seedOf(name: string): string {
  return name.replace(/(_hr\.dat|_wsvec\.dat|_centres\.xyz|\.wout|\.win)$/i, "").replace(/[._-](up|dn|down)$/i, "");
}
